'use client';

import { c as _c } from "react/compiler-runtime";
import { RefreshRouteOnSave as PayloadLivePreview } from '@payloadcms/live-preview-react';
import { useRouter } from 'next/navigation';
import React, { useCallback, useEffect, useRef } from 'react';

/**
 * Returns a debounced refresh handler that calls `router.refresh()` after the specified delay.
 * Any pending timeout is cleared when the component using this hook unmounts.
 *
 * @param delayMs - Debounce delay in milliseconds before refreshing the route.
 * @returns A debounced refresh callback.
 */
function useDebouncedRouteRefresh(delayMs) {
  const $ = _c(5);
  const router = useRouter();
  const timerReference = useRef(null);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => () => {
      if (timerReference.current) {
        clearTimeout(timerReference.current);
      }
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  let t2;
  if ($[2] !== delayMs || $[3] !== router) {
    t2 = () => {
      if (timerReference.current) {
        clearTimeout(timerReference.current);
      }
      timerReference.current = setTimeout(() => {
        router.refresh();
      }, delayMs);
    };
    $[2] = delayMs;
    $[3] = router;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const handleRefresh = t2;
  return handleRefresh;
}

/**
 * Client component that wires Payload live preview to a debounced Next.js route refresh.
 */
export const RefreshRouteOnSave: React.FC<{
  serverURL: string;
}> = t0 => {
  const $ = _c(3);
  const {
    serverURL
  } = t0;
  const handleRefresh = useDebouncedRouteRefresh(250);
  let t1;
  if ($[0] !== handleRefresh || $[1] !== serverURL) {
    t1 = <PayloadLivePreview refresh={handleRefresh} serverURL={serverURL} />;
    $[0] = handleRefresh;
    $[1] = serverURL;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};